/**
 * Pack Builder Engine — server functions.
 *
 * Read-only and compute-only in Phase 1: no admin CRUD, no storefront route,
 * no cart/checkout/order interaction. Published packs are public catalog data.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { PackSelection } from "./types";

export const listPacksFn = createServerFn({ method: "GET" }).handler(async () => {
  const server = await import("./packs.server");
  try {
    return { packs: await server.listPublishedPacks(), error: null as string | null };
  } catch (err) {
    return { packs: [], error: err instanceof Error ? err.message : "Unable to load packs." };
  }
});

export const getPackBySlugFn = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data }) => {
    const server = await import("./packs.server");
    try {
      return { pack: await server.getPublishedPackDetail(data.slug), error: null as string | null };
    } catch (err) {
      return { pack: null, error: err instanceof Error ? err.message : "Unable to load pack." };
    }
  });

const productQuerySchema = z.object({
  packSlug: z.string().min(1),
  search: z.string().optional(),
  family: z.string().optional(),
  collectionId: z.string().uuid().optional(),
  availability: z.enum(["all", "in_stock"]).optional(),
  minPrice: z.number().nonnegative().optional(),
  maxPrice: z.number().nonnegative().optional(),
  sort: z.enum(["popularity", "newest", "alphabetical", "price_asc", "price_desc"]).optional(),
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(96).optional(),
});

export const listPackProductsFn = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => productQuerySchema.parse(input))
  .handler(async ({ data }) => {
    const server = await import("./packs.server");
    try {
      return { ...(await server.listPackProductsFromDatabase(data)), error: null as string | null };
    } catch (err) {
      return {
        pack: null,
        items: [],
        total: 0,
        page: data.page ?? 1,
        pageSize: data.pageSize ?? 24,
        hasMore: false,
        error: err instanceof Error ? err.message : "Unable to load pack products.",
      };
    }
  });

const selectionSchema = z.object({
  packSlug: z.string().min(1),
  variantId: z.string().uuid(),
  selection: z.record(z.string(), z.number().int().min(0)),
  addons: z.record(z.string(), z.number().int().min(0)).optional(),
  gift: z
    .object({
      recipientName: z.string().max(120).optional(),
      senderName: z.string().max(120).optional(),
      message: z.string().max(1000).optional(),
      hidePrices: z.boolean().optional(),
    })
    .optional(),
});

/** Authoritative server-side validation of a pack composition. */
export const validatePackSelectionFn = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => selectionSchema.parse(input))
  .handler(async ({ data }) => {
    const server = await import("./packs.server");
    const composition = await import("./composition");
    const { pack, catalog } = await server.packCompositionContext(data.packSlug);
    const variant = pack?.variants.find((v) => v.id === data.variantId);
    if (!pack || !variant) {
      return {
        result: null,
        error: "This pack is not available.",
      };
    }
    const selection = pruneSelection(data.selection);
    return {
      result: composition.validateComposition(pack, variant, selection, catalog, {
        fixedItems: pack.fixedItems,
        addons: pack.addons,
        addonSelection: data.addons ?? {},
        giftMessage: data.gift?.message ?? "",
      }),
      error: null as string | null,
    };
  });

/** Authoritative server-side price for a pack composition. */
export const pricePackSelectionFn = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => selectionSchema.parse(input))
  .handler(async ({ data }) => {
    const server = await import("./packs.server");
    const composition = await import("./composition");
    const pricing = await import("./pricing");
    const { pack, catalog } = await server.packCompositionContext(data.packSlug);
    const variant = pack?.variants.find((v) => v.id === data.variantId);
    if (!pack || !variant) return { pricing: null, validation: null, fixedItems: [], error: "This pack is not available." };
    const selection = pruneSelection(data.selection);
    const addonSelection = pruneSelection(data.addons ?? {});
    const gift = data.gift
      ? {
          recipientName: data.gift.recipientName ?? "",
          senderName: data.gift.senderName ?? "",
          message: data.gift.message ?? "",
          hidePrices: Boolean(data.gift.hidePrices),
        }
      : null;
    return {
      pricing: pricing.resolveComposedPricing(pack, variant, selection, catalog, {
        addons: pack.addons,
        addonSelection,
        gift,
      }),
      validation: composition.validateComposition(pack, variant, selection, catalog, {
        fixedItems: pack.fixedItems,
        addons: pack.addons,
        addonSelection,
        giftMessage: gift?.message ?? "",
      }),
      fixedItems: pack.fixedItems.filter((i) => !i.variantId || i.variantId === variant.id),
      error: null as string | null,
    };
  });

function pruneSelection(selection: Record<string, number>): PackSelection {
  return Object.fromEntries(Object.entries(selection).filter(([, qty]) => qty > 0));
}